/**
 * URL contract linking the two pages of the supporter-voices video flow. The confirmation is a
 * separate page, so the actions page unmounts on submit and only the URL survives the round trip.
 *
 * Functions rather than exported constants, so no caller has to know a param name. The exception is
 * `SUPPORTER_VOICE_ENTRY_PARAM` in `shared/constants`, which the actions page needs as a raw key.
 */

import { SUPPORTER_VOICE_ENTRY_PARAM } from '#src/app/shared/constants';

import { parseNextStep } from './parseNextStep';
import type { VideoConfirmationNextStep } from './parseNextStep';

export type { VideoConfirmationNextStep };

/** Set by the actions page on the way out; read by `petitionVideo` to decide what it offers next. */
const NEXT_STEP_PARAM = 'next';

/** Takes the params rather than the value, so the param name stays private to this module. */
export function parseNextStepFromParams(params: URLSearchParams): VideoConfirmationNextStep | undefined {
	return parseNextStep(params.get(NEXT_STEP_PARAM) ?? undefined);
}

const COMMENT_AFTER_VIDEO_ENTRY = 'comment_after_video';

/**
 * Whether an actions-page visit is the return leg of the comment-after-video flow. A stale or
 * hand-edited param opens the page on the video ask rather than half-entering a flow.
 */
export function isCommentAfterVideoEntry(entryParamValue: string | undefined): boolean {
	return entryParamValue === COMMENT_AFTER_VIDEO_ENTRY;
}

/** Where a submitted video lands. */
export function buildVideoConfirmationUrl({
	slug,
	videoId,
	nextStep,
}: {
	slug: string;
	videoId: string;
	nextStep: VideoConfirmationNextStep | undefined;
}): string {
	const base = `/p/${slug}/v/${videoId}?success=true`;

	return nextStep ? `${base}&${NEXT_STEP_PARAM}=${nextStep}` : base;
}

/**
 * Back to the actions page on the comment step. That page strips the param as it reads it, so a
 * reload cannot re-enter a step the supporter has already left.
 */
export function buildCommentAfterVideoUrl(slug: string): string {
	return `/p/${slug}/actions?${SUPPORTER_VOICE_ENTRY_PARAM}=${COMMENT_AFTER_VIDEO_ENTRY}`;
}
